import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Users, Mail, UserCheck, FileText, CreditCard,
  TrendingDown, Search, Filter, ChevronDown, ExternalLink
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogoFull } from '@/components/Logo';
import { api } from '@/services/api';

const STAGE_CONFIG = {
  lead_captured:   { label: 'Lead Captured',   color: '#00AEEF', icon: Mail,      order: 1 },
  registered:      { label: 'Registered',       color: '#3D5A80', icon: UserCheck, order: 2 },
  policy_created:  { label: 'Policy Created',   color: '#F59E0B', icon: FileText,  order: 3 },
  policy_active:   { label: 'Policy Active',    color: '#00C4AD', icon: FileText,  order: 4 },
  purchased:       { label: 'Purchased',         color: '#83CF44', icon: CreditCard, order: 5 },
};

const FUNNEL_COLORS = ['#1A3A64', '#00AEEF', '#3D5A80', '#F59E0B', '#83CF44'];

const FunnelBar = ({ stage, maxCount }) => {
  const pct = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
  return (
    <div className="flex items-center gap-4" data-testid={`funnel-stage-${stage.stage}`}>
      <div className="w-32 text-right">
        <p className="text-sm font-medium text-[#0D1B2A]">{stage.label}</p>
      </div>
      <div className="flex-1 h-10 bg-[#E8EEF4] rounded-lg overflow-hidden relative">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${Math.max(pct, 2)}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="h-full rounded-lg flex items-center justify-end pr-3"
          style={{ backgroundColor: stage.color }}
        >
          {pct > 15 && <span className="text-white text-sm font-bold">{stage.count}</span>}
        </motion.div>
        {pct <= 15 && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm font-bold text-[#3D5A80]">{stage.count}</span>}
      </div>
    </div>
  );
};

const LeadsDashboard = () => {
  const [funnel, setFunnel] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const [funnelData, leadsData] = await Promise.all([
          api.getLeadsFunnel(),
          api.getLeads()
        ]);
        setFunnel(funnelData);
        setLeads(leadsData);
      } catch (err) {
        console.error('Leads load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = leads.filter(l => {
    const matchSearch = !search || l.email?.toLowerCase().includes(search.toLowerCase()) || l.name?.toLowerCase().includes(search.toLowerCase());
    const matchStage = stageFilter === 'all' || l.stage === stageFilter;
    return matchSearch && matchStage;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full" />
      </div>
    );
  }

  const funnelStages = funnel?.funnel || [];
  const maxCount = Math.max(...funnelStages.map(s => s.count), 1);
  const rates = funnel?.conversion_rates || {};
  const topIndustries = funnel?.top_industries || [];

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="leads-dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-[#DCE4ED] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-[#8DA0B9] hover:text-[#0D1B2A] transition-colors" data-testid="back-to-admin">
              <ArrowLeft size={18} /><span className="text-sm">Admin</span>
            </Link>
            <div className="w-px h-6 bg-[#DCE4ED]" />
            <h1 className="text-lg font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Leads & Funnel</h1>
          </div>
          <LogoFull size="small" />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Metric Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {funnelStages.map((s, i) => (
            <motion.div key={s.stage} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white rounded-xl border border-[#DCE4ED] p-4" data-testid={`metric-${s.stage}`}>
              <p className="text-xs text-[#8DA0B9] mb-1">{s.label}</p>
              <p className="text-2xl font-bold" style={{ color: FUNNEL_COLORS[i] }}>{s.count}</p>
              {i > 0 && funnelStages[i - 1].count > 0 && (
                <p className="text-xs mt-1 flex items-center gap-1">
                  <TrendingDown size={12} className="text-[#8DA0B9]" />
                  <span className="text-[#3D5A80] font-medium">
                    {Math.round(s.count / funnelStages[i - 1].count * 100)}%
                  </span>
                  <span className="text-[#8DA0B9]">from prev</span>
                </p>
              )}
            </motion.div>
          ))}
        </div>

        {/* Funnel + Conversion Grid */}
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Funnel Visualization */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-[#DCE4ED] p-6" data-testid="funnel-chart">
            <h2 className="text-base font-semibold text-[#0D1B2A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Conversion Funnel</h2>
            <div className="space-y-4">
              {funnelStages.map((s, i) => (
                <FunnelBar key={s.stage} stage={{ ...s, color: FUNNEL_COLORS[i] }} maxCount={maxCount} />
              ))}
            </div>
            {/* Conversion arrows */}
            <div className="mt-6 pt-4 border-t border-[#DCE4ED] grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Quote → Lead', rate: rates.quote_to_lead },
                { label: 'Lead → Register', rate: rates.lead_to_register },
                { label: 'Register → Policy', rate: rates.register_to_policy },
                { label: 'Policy → Paid', rate: rates.policy_to_paid },
              ].map(r => (
                <div key={r.label} className="text-center p-2 bg-[#F0F4F8] rounded-lg">
                  <p className="text-xs text-[#8DA0B9]">{r.label}</p>
                  <p className="text-lg font-bold text-[#1A3A64]">{r.rate || 0}%</p>
                </div>
              ))}
            </div>
          </div>

          {/* Top Industries */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-[#DCE4ED] p-6" data-testid="top-industries">
            <h2 className="text-base font-semibold text-[#0D1B2A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Top Industries</h2>
            {topIndustries.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={topIndustries} layout="vertical" margin={{ left: 0, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8EEF4" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#8DA0B9' }} />
                  <YAxis type="category" dataKey="industry" width={90} tick={{ fontSize: 11, fill: '#3D5A80' }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #DCE4ED', fontSize: 12 }} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {topIndustries.map((_, i) => <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-[#8DA0B9]">No industry data yet</p>
            )}
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl border border-[#DCE4ED] overflow-hidden" data-testid="leads-table">
          <div className="p-4 border-b border-[#DCE4ED] flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <h2 className="text-base font-semibold text-[#0D1B2A] flex-shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
              All Leads ({filtered.length})
            </h2>
            <div className="flex-1 flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 max-w-xs">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8DA0B9]" />
                <Input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search by email or name..."
                  className="pl-9 border-[#DCE4ED] h-9 text-sm"
                  data-testid="leads-search"
                />
              </div>
              <div className="relative">
                <select
                  value={stageFilter} onChange={e => setStageFilter(e.target.value)}
                  className="border border-[#DCE4ED] rounded-lg px-3 py-2 text-sm appearance-none bg-white pr-8"
                  data-testid="leads-stage-filter"
                >
                  <option value="all">All Stages</option>
                  {Object.entries(STAGE_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8DA0B9] pointer-events-none" />
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Users size={32} className="mx-auto text-[#DCE4ED] mb-3" />
              <p className="text-sm text-[#8DA0B9]">No leads yet. They'll appear once visitors use the Quote Calculator.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F0F4F8]/60">
                    <th className="text-left px-4 py-3 font-medium text-[#3D5A80]">Lead</th>
                    <th className="text-left px-4 py-3 font-medium text-[#3D5A80]">Stage</th>
                    <th className="text-left px-4 py-3 font-medium text-[#3D5A80] hidden md:table-cell">Quote</th>
                    <th className="text-left px-4 py-3 font-medium text-[#3D5A80] hidden lg:table-cell">Industry</th>
                    <th className="text-left px-4 py-3 font-medium text-[#3D5A80]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead, i) => {
                    const cfg = STAGE_CONFIG[lead.stage] || STAGE_CONFIG.lead_captured;
                    const StageIcon = cfg.icon;
                    return (
                      <tr key={lead.lead_id || i} className="border-t border-[#E8EEF4] hover:bg-[#F0F4F8]/30 transition-colors" data-testid={`lead-row-${i}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-[#0D1B2A]">{lead.name || 'Anonymous'}</p>
                          <p className="text-xs text-[#8DA0B9]">{lead.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
                            <StageIcon size={12} />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {lead.quote_summary ? (
                            <div>
                              <p className="font-medium text-[#1A3A64]">${Math.round(lead.quote_summary.monthly_premium)}/mo</p>
                              <p className="text-xs text-[#8DA0B9]">{lead.quote_coverages_count} coverages</p>
                            </div>
                          ) : (
                            <span className="text-[#8DA0B9]">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-[#3D5A80] capitalize">{lead.quote_industry || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[#8DA0B9]">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadsDashboard;
